// コメント本文の表示コンポーネント。
// - CommentMessageBody: 本文（生の Markdown テキスト）を、付箋・テキストと同じ行単位ブロック記法
//   （見出し # 〜 ######・箇条書き - / * / +（ネスト対応）・引用 >・水平線 ---・番号付きリスト 1.）で
//   整形表示する。スタイル・表示テキストの決定は markdown-text.ts（付箋・テキストの
//   MarkdownTextView と同じ単一の真実）へ集約し、各行の本文はコメント専用の
//   LinkifiedCommentText でリンク化する。
//   記法を含まない本文は全行 plain の div となり、従来の見た目（pre-wrap のプレーン表示 +
//   リンク化）と一致する。保存は付箋・テキストと同じく生 Markdown のまま（表示時のみ整形）。
// - LinkifiedCommentText: コメント本文の URL 自動リンク化（従来 comment-thread-popover.tsx 内に
//   あったものを、Markdown 整形表示との共用のため本ファイルへ移設）。

import { Fragment, type CSSProperties } from "react";
import { buildMarkdownSourceMap, type TextInlineDisplaySpan } from "@refinement-canvas/domain";
import { markdownBlockPreviousKinds } from "../../../lib/markdown-text";
import { isExternalHttpUrl } from "../../../lib/linkify-text";
import { TEXT_DEFAULT_COLOR } from "../../../lib/text-color";
import {
  resolveMarkdownTableLinkColorVars,
  resolveTableBodyPalette,
  type MarkdownTableBodyCellContext,
} from "../../../lib/text-link-color";
import { ExternalLinkIcon, renderInlineDecoratedSpans } from "../objects/linkified-text";
import { MarkdownCalloutView } from "../objects/markdown-callout-view";
import { MarkdownCodeBlockView } from "../objects/markdown-code-block-view";
import { MarkdownLineView, type MarkdownInlineRenderer } from "../objects/markdown-line-view";
import { MarkdownTableView } from "../objects/markdown-table-view";
import { useCanvasStore } from "../store/canvas-store";

interface Props {
  readonly text: string;
  /**
   * 表の使用可能幅（em）。コメント本文の領域幅（ポップオーバーの固定幅から内側余白を引いた値）と
   * 本文の文字サイズから、呼び出し側が `markdownTableAvailableEm` で求めて渡す。
   */
  readonly availableEm: number;
}

/** コメント本文は常に通常本文色・通常表配色で描画する。 */
const COMMENT_TABLE_BODY_PALETTE = resolveTableBodyPalette({
  bodyColor: TEXT_DEFAULT_COLOR,
  fill: undefined,
});

// コメント本文（生の Markdown テキスト）の整形表示。
// 付箋・テキストの MarkdownTextView と同じ行構造（1 行 = 1 div、スタイルは markdown-text.ts の
// 単一の真実）で描画するが、リンクはキャンバスオブジェクト用 LinkifiedText（250ms 遅延オープン・
// ダブルクリック編集への譲歩）ではなく、コメント専用の LinkifiedCommentText（<a> の標準挙動 +
// stopPropagation）を使う。見出しの fontSize / 箇条書きインデントは em 単位のため、コメントの
// フォントサイズ（14px）に相対でスケールする。
// 表（GFM 基本形）も付箋・テキストと同じ MarkdownTableView で描き、セルの中身にはコメント専用の
// LinkifiedCommentText を渡す（表の中と外でコメントのリンク挙動を変えない）。
export function CommentMessageBody({ text, availableEm }: Props): JSX.Element {
  const sourceMap = buildMarkdownSourceMap(text);
  // 連続する箇条書きの項目間隔は、付箋・テキストと同じ共有関数で決める（表の直後は付かない）。
  const previousKinds = markdownBlockPreviousKinds(sourceMap);
  // 行・表のセル・callout のタイトルは、いずれもコメント専用のリンク描画で描く
  // （キャンバスオブジェクト用の StyledLinkifiedText へ寄せるとコメント内リンクの挙動が変わる）。
  const renderInline: MarkdownInlineRenderer = (props) => <LinkifiedCommentText {...props} />;
  return (
    <>
      {sourceMap.blocks.map((block, blockIndex) => {
        if (block.kind === "table") {
          return (
            <MarkdownTableView
              key={`table-${block.table.headerLineIndex}`}
              availableEm={availableEm}
              bodyColor={TEXT_DEFAULT_COLOR}
              bodyPalette={COMMENT_TABLE_BODY_PALETTE}
              renderCell={(spans, context) => (
                <LinkifiedCommentText
                  spans={spans}
                  tableBodyContext={context.kind === "body" ? context : undefined}
                />
              )}
              table={block.table}
              text={text}
            />
          );
        }
        if (block.kind === "code") {
          return (
            <MarkdownCodeBlockView
              key={`code-${block.code.fenceStartLineIndex}`}
              code={block.code}
              sourceMap={sourceMap}
            />
          );
        }
        if (block.kind === "callout") {
          return (
            <MarkdownCalloutView
              key={`callout-${block.callout.openLineIndex}`}
              callout={block.callout}
              renderInline={renderInline}
              sourceMap={sourceMap}
            />
          );
        }
        return (
          // 行は本文位置と 1:1 対応の静的リストで並べ替えが起きないため行 index の key で問題ない。
          <MarkdownLineView
            key={block.lineIndex}
            lineIndex={block.lineIndex}
            previousKind={previousKinds[blockIndex]}
            renderInline={renderInline}
            sourceMap={sourceMap}
          />
        );
      })}
    </>
  );
}

// コメント本文の表示区間（記法の装飾つき）を描画し、URL を自動リンク化する。
// 装飾つき区間の描画（renderInlineDecoratedSpans）だけをオブジェクト側と共用し、アンカーの
// 属性とハンドラはコメント専用のまま残す（統合するとコメントのリンク挙動が変わるため）。
// キャンバス上のオブジェクト用 StyledLinkifiedText とは異なり、コメントはダブルクリック編集が
// 無いため遅延オープンは行わず、<a> の標準挙動で開く。内部/外部の判定・外部アイコン・bare URL の
// 省略表示（title でフル URL 確認可）はオブジェクト側と同じ仕様・同じ CSS クラスへ揃える。
// 参照専用（埋め込み iframe）では内部リンクの同一タブ遷移が iframe の内容を置き換えて
// しまうため、オブジェクト側 openUrl と同様に readOnly 時は内部リンクも新規タブで開く。
export function LinkifiedCommentText({
  spans,
  prefix,
  prefixClassName,
  prefixStyle,
  headingLine,
  tableBodyContext,
}: {
  readonly spans: readonly TextInlineDisplaySpan[];
  /** 箇条書きのグリフや番号付きリストの項番など、装飾を持たない表示専用の接頭辞。 */
  readonly prefix?: string | undefined;
  /** 接頭辞の span に付けるクラス（番号付きリストの項番 span など）。省略時は属性を付けない。 */
  readonly prefixClassName?: string | undefined;
  /** 接頭辞の span へ与えるインラインスタイル（マーカー領域の幅）。省略時は属性を付けない。 */
  readonly prefixStyle?: CSSProperties | undefined;
  readonly headingLine?: boolean | undefined;
  readonly tableBodyContext?: MarkdownTableBodyCellContext | undefined;
}): JSX.Element {
  const readOnly = useCanvasStore((s) => s.readOnly);
  const renderPieces = (group: readonly TextInlineDisplaySpan[]): JSX.Element[] =>
    renderInlineDecoratedSpans(group, {
      headingLine,
      tableBodyContext,
      link: group.some((span) => span.url !== undefined),
    });
  return (
    <>
      {prefix !== undefined && prefix.length > 0 ? (
        <span className={prefixClassName} style={prefixStyle}>
          {prefix}
        </span>
      ) : null}
      {groupCommentSpansByLink(spans).map((group, index) => {
        const url = group.url;
        if (url === undefined) {
          // key はグループ列のインデックス。spans は本文から毎回同じ順序で導出されるため安定。
          return <Fragment key={index}>{renderPieces(group.spans)}</Fragment>;
        }
        const external = isExternalHttpUrl(url);
        const newTab = external || readOnly;
        // 表示テキストが URL そのもの（自動リンク化）の場合のみ 1 行省略表示の対象にする。
        const displayText = group.spans.map((span) => span.text).join("");
        const bareUrl = displayText === url;
        const className = [
          "canvas-linkified-link",
          "comment-linkified-link",
          ...(bareUrl ? ["canvas-linkified-link--url"] : []),
        ].join(" ");
        const tableLinkStyle =
          tableBodyContext === undefined
            ? undefined
            : (resolveMarkdownTableLinkColorVars({
                effectiveBackground: tableBodyContext.background,
                onDark: tableBodyContext.palette.onDark,
              }) as CSSProperties);
        return (
          <a
            key={index}
            className={className}
            href={url}
            target={newTab ? "_blank" : undefined}
            rel={newTab ? "noopener noreferrer" : undefined}
            title={bareUrl ? url : undefined}
            // リンククリックがポップオーバーの pointerdown 系ハンドラやキャンバスへ波及しないようにする。
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            style={tableLinkStyle}
          >
            {bareUrl ? (
              <span className="canvas-linkified-link-label">{renderPieces(group.spans)}</span>
            ) : (
              renderPieces(group.spans)
            )}
            {external ? <ExternalLinkIcon /> : null}
          </a>
        );
      })}
    </>
  );
}

/**
 * 分割結果を「連続する同じ URL（および非リンク）」でまとめる。
 * 1 つのリンクを装飾境界で複数の `<a>` へ割ると、素 URL の 1 行省略表示・`title`・
 * 外部リンクアイコンが区間ごとの判定になってしまうため、リンクは 1 グループ = 1 anchor で描く。
 */
function groupCommentSpansByLink(
  spans: readonly TextInlineDisplaySpan[]
): readonly { readonly url: string | undefined; readonly spans: TextInlineDisplaySpan[] }[] {
  const groups: { url: string | undefined; spans: TextInlineDisplaySpan[] }[] = [];
  for (const span of spans) {
    const last = groups[groups.length - 1];
    if (last && last.url === span.url) {
      last.spans.push(span);
      continue;
    }
    groups.push({ spans: [span], url: span.url });
  }
  return groups;
}
